import type { ExpandedChangedEvent, ICellRendererParams } from 'ag-grid-community';
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Flex } from '../../Core';
import { Icon, IconName } from '../../Icons';
import { Text } from '../../Text';

/**
 * Group row renderer used by `MobileDataTable` (registered via `groupRowRenderer`).
 * Tap toggles expansion; the rest of the group expansion machinery
 * (`isGroupOpenByDefault`, `usePersistedRowGroupExpansion`) is unchanged.
 */
export function MobileGroupCard(params: ICellRendererParams) {
  const { node } = params;
  const [expanded, setExpanded] = useState(node.expanded);

  useEffect(() => {
    const listener = (event: ExpandedChangedEvent) => setExpanded(event.node.expanded);
    node.addEventListener('expandedChanged', listener);
    return () => node.removeEventListener('expandedChanged', listener);
  }, [node]);

  const onToggle = useCallback(() => node.setExpanded(!node.expanded), [node]);
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    },
    [onToggle]
  );
  const childCount = node.allChildrenCount ?? node.childrenAfterFilter?.length ?? 0;
  const label = node.key ?? '';

  return (
    <Container onClick={onToggle} onKeyDown={onKeyDown} role="button" tabIndex={0} aria-expanded={expanded}>
      <Flex gap="spacing8" alignItems="center">
        <Icon
          size="fontSizeSmall"
          icon={IconName.ChevronRight}
          style={{
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 100ms linear',
          }}
        />
        <Text color="colorTextImportant">{label}</Text>
      </Flex>
      {childCount > 0 && <Text color="colorTextSubtle">{childCount}</Text>}
    </Container>
  );
}

const Container = styled(Flex)`
  padding: 0 ${({ theme }) => theme.spacing16}px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  cursor: pointer;
  background-color: ${({ theme }) => theme.backgroundContent};
`;
